import { styled, keyframes, alpha } from '@mui/material/styles';
import { Box, Typography, Button, Paper, Dialog } from '@mui/material';

const cardAppear = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const glowPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 8px rgba(74, 179, 188, 0.15);
  }
  50% {
    box-shadow: 0 0 16px rgba(74, 179, 188, 0.3);
  }
`;

export const highlightInward = keyframes`
  0% {
    box-shadow: 0 0 30px 12px rgba(255, 183, 77, 0.3), 0 0 60px 20px rgba(255, 183, 77, 0.12);
  }
  30% {
    box-shadow: 0 0 16px 6px rgba(255, 183, 77, 0.25), 0 0 32px 10px rgba(255, 183, 77, 0.08);
  }
  60% {
    box-shadow: 0 0 6px 2px rgba(255, 183, 77, 0.15);
  }
  100% {
    box-shadow: 0 1px 2px rgba(0,0,0,0.06);
  }
`;

export const highlightFlash = keyframes`
  0% { opacity: 0; }
  40% { opacity: 0; }
  45% { opacity: 0.15; }
  50% { opacity: 0; }
  100% { opacity: 0; }
`;

export const highlightBadgePop = keyframes`
  0% { transform: scale(0) rotate(-30deg); opacity: 0; }
  8% { transform: scale(1.3) rotate(5deg); opacity: 1; }
  14% { transform: scale(0.9) rotate(-3deg); }
  20% { transform: scale(1.05) rotate(1deg); }
  26% { transform: scale(1) rotate(0deg); }
  80% { transform: scale(1) rotate(0deg); opacity: 1; }
  90% { transform: scale(1) rotate(0deg); opacity: 0.5; }
  100% { transform: scale(0.6) rotate(10deg); opacity: 0; }
`;

export const highlightBadgePulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(255, 183, 77, 0.35); }
  50% { box-shadow: 0 0 0 4px rgba(255, 183, 77, 0); }
`;

// ============================================
// CancelType Dialog
// ============================================
export const CancelTypeStyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: '10px',
    overflow: 'hidden',
  },
});

export const CancelTypeRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '24px',
});

export const CancelTypeLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#333',
  minWidth: '60px',
});

// ============================================
// Container & Layout
// ============================================
export const PageContainer = styled(Paper)({
  borderRadius: '10px',
  width: '100%',
  position: 'relative',
  height: 'calc(100dvh - 140px)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

export const ContentWrapper = styled(Box)({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
});

// ============================================
// Header Controls
// ============================================
export const HeaderContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  marginBottom: '8px',
});

export const HeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  minHeight: '32px',
});

export const LeftControlGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
});

export const CenterControlGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

export const RightControlGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

// ============================================
// Buttons
// ============================================
export const PrimaryButton = styled(Button)({
  fontSize: '12px',
  fontWeight: 600,
  background: '#4AB3BC',
  color: '#fff',
  border: 'none',
  height: '28px',
  borderRadius: '6px',
  padding: '0 12px',
  minWidth: 0,
  '&:hover': {
    background: '#3da3ab',
  },
});

export const TemplateButton = styled(Button)({
  fontSize: '12px',
  fontWeight: 600,
  background: '#333',
  color: '#fff',
  border: 'none',
  height: '28px',
  borderRadius: '6px',
  padding: '0 12px',
  minWidth: 0,
  '&:hover': {
    background: '#555',
  },
});

// 캘린더 헤더 컨트롤(TodayButton, NavButton, ZoomButton, ZoomButtonGroup, ZoomLabel)은
// ../LessonCalendarStyled.js로 이동되어 private lesson과 공유됨

export const CopyButton = styled(Button)({
  background: '#f1f4f6',
  color: '#555',
  '&:hover': { background: '#e4e7ea' },
  borderRadius: '6px',
  padding: '0 10px',
  minWidth: 0,
  height: '28px',
  fontSize: '12px',
  fontWeight: 600,
});

export const DeleteButton = styled(Button)({
  background: '#f44336',
  border: '1px solid #f44336',
  color: '#fff',
  '&:hover': {
    background: 'none',
    color: '#f44336',
  },
  marginLeft: '10px',
});

// ============================================
// Week Title
// ============================================
export const WeekTitle = styled(Typography)({
  margin: '0 6px',
  fontSize: '18px',
  fontWeight: 700,
  minWidth: '160px',
  textAlign: 'center',
  color: '#111827',
  letterSpacing: '-0.3px',
});

// ============================================
// Status Filter Box
// ============================================
export const StatusFilterBox = styled(Box, {
  shouldForwardProp: prop => prop !== 'isActive' && prop !== 'statusColor',
})(({ isActive, statusColor }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '3px 8px',
  cursor: 'pointer',
  backgroundColor: isActive ? `${statusColor}1a` : '#f8f8f8',
  borderRadius: '6px',
  border: isActive ? `1.5px solid ${statusColor}` : '1.5px solid transparent',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: `${statusColor}14`,
  },
}));

export const StatusFilterBoxWithBorder = styled(StatusFilterBox)({});

export const StatusIndicator = styled(Typography, {
  shouldForwardProp: prop => prop !== 'statusColor' && prop !== 'isActive',
})(({ statusColor, isActive }) => ({
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  background: statusColor,
  boxShadow: isActive ? `0 0 6px ${statusColor}99` : 'none',
  transition: 'box-shadow 0.2s ease',
  flexShrink: 0,
}));

export const StatusLabel = styled(Typography, {
  shouldForwardProp: prop => prop !== 'activeColor' && prop !== 'isActive',
})(({ activeColor, isActive }) => ({
  fontSize: '11px',
  fontWeight: 500,
  color: isActive ? activeColor : '#666',
  transition: 'color 0.2s ease',
}));

export const StatusCount = styled(Typography, {
  shouldForwardProp: prop => prop !== 'activeColor' && prop !== 'isActive',
})(({ activeColor, isActive }) => ({
  fontSize: '12px',
  fontWeight: 700,
  color: isActive ? activeColor : '#333',
  transition: 'color 0.2s ease',
}));

export const StatusRow = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  gap: '6px',
  alignItems: 'center',
});

export const StatusCountRow = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  marginLeft: '4px',
});

// ============================================
// Calendar Table
// ============================================
export const CalendarContainer = styled(Box, {
  shouldForwardProp: prop => prop !== 'isDragging',
})(({ isDragging }) => ({
  width: '100%',
  flex: 1,
  minHeight: 0,
  overflowX: 'hidden',
  overflowY: 'auto',
  userSelect: isDragging ? 'none' : 'auto',
  border: '1px solid #E5E7EB',
  borderRadius: '10px',
  background: '#fff',
}));

export const CalendarTable = styled('table')({
  width: '100%',
  tableLayout: 'fixed',
  borderCollapse: 'separate',
  borderSpacing: 0,
});

export const TableHeaderCell = styled('th', {
  shouldForwardProp: prop => !['isToday', 'isTimeColumn', 'cellWidth'].includes(prop),
})(({ isToday, isTimeColumn }) => ({
  textAlign: 'center',
  borderBottom: '2px solid #e0e0e0',
  borderRight: '1px solid #f0f0f0',
  background: isToday ? '#e8f5f5' : '#fafafa',
  padding: isTimeColumn ? '10px 0' : '8px 4px',
  width: isTimeColumn ? '56px' : 'auto',
  minWidth: isTimeColumn ? '56px' : 'auto',
  maxWidth: isTimeColumn ? '52px' : 'none',
  position: 'sticky',
  top: isTimeColumn ? 0 : -1,
  left: isTimeColumn ? 0 : 'auto',
  zIndex: isTimeColumn ? 100 : 1000,
  '&:last-child': { borderRight: 'none' },
}));

export const DayLabel = styled('div', {
  shouldForwardProp: prop => prop !== 'isWeekend' && prop !== 'isSunday',
})(({ isWeekend, isSunday }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '5px',
  fontSize: '14px',
  fontWeight: 600,
  color: isSunday ? '#DC2626' : isWeekend ? '#2563EB' : '#4B5563',
  letterSpacing: '-0.2px',
}));

export const DateLabel = styled('div', {
  shouldForwardProp: prop => prop !== 'isWeekend' && prop !== 'isSunday',
})(({ isWeekend, isSunday }) => ({
  fontSize: '18px',
  fontWeight: 700,
  color: isSunday ? '#E53935' : isWeekend ? '#1565C0' : '#333',
}));

export const TodayBadge = styled('span')({
  fontSize: '9px',
  fontWeight: 600,
  color: 'rgba(255,255,255,0.95)',
  background: 'linear-gradient(135deg, #4AB3BC 0%, #0F7F85 100%)',
  padding: '1px 6px',
  borderRadius: '6px',
  letterSpacing: '0.3px',
  lineHeight: '16px',
});

export const TimeCell = styled('td', {
  shouldForwardProp: prop => prop !== 'cellHeight',
})(({ cellHeight = 80 }) => ({
  textAlign: 'center',
  borderBottom: '1px solid #F3F4F6',
  borderRight: '1px solid #E5E7EB',
  background: '#FAFBFC',
  fontSize: '11px',
  fontWeight: 500,
  color: '#9CA3AF',
  padding: '4px',
  position: 'sticky',
  left: 0,
  zIndex: 999,
  height: `${cellHeight}px`,
  width: '52px',
  minWidth: '52px',
  maxWidth: '52px',
}));

export const EventCell = styled('td', {
  shouldForwardProp: prop => prop !== 'cellWidth' && prop !== 'cellHeight',
})(({ cellWidth, cellHeight = 80 }) => ({
  position: 'relative',
  height: `${cellHeight}px`,
  borderBottom: '1px solid #f0f0f0',
  borderRight: '1px solid #f0f0f0',
  verticalAlign: 'top',
  cursor: 'pointer',
  overflow: 'visible',
  '&:last-child': { borderRight: 'none' },
  // 빈칸 hover 박스 — 선택 단위 구간. 마우스 이동 때 셀에 --hover-top/--hover-height(px)를 직접 넣는다.
  // 값이 없으면 셀 전체(기존 모양). 구간이 셀 밖으로 걸치면 위·아래 행까지 넘어 그린다.
  '&:hover:not(:has(.event-card:hover))::after': {
    content: '""',
    position: 'absolute',
    top: 'calc(var(--hover-top, 0px) + 2px)',
    left: '2px',
    right: '2px',
    height: 'calc(var(--hover-height, 100%) - 4px)',
    boxSizing: 'border-box',
    backgroundColor: 'rgba(15, 127, 133, 0.04)',
    border: '1.5px dashed rgba(15, 127, 133, 0.25)',
    borderRadius: '6px',
    pointerEvents: 'none',
    zIndex: 0,
  },
}));

export const RowDivider = styled('div')({
  position: 'absolute',
  left: 0,
  right: 0,
  top: '50%',
  height: '1px',
  backgroundColor: '#F3F4F6',
  pointerEvents: 'none',
  zIndex: 5,
});

// ============================================
// Event Card - 가로 레이아웃 (1~3개)
// ============================================
export const EventCardHorizontal = styled('div', {
  shouldForwardProp: prop =>
    !['statusColor', 'cardWidth', 'cardHeight', 'cardTop', 'cardLeft', 'cardZIndex', 'isHighlighted'].includes(prop),
})(({ statusColor, cardWidth, cardHeight, cardTop, cardLeft, cardZIndex, isHighlighted }) => ({
  position: 'absolute',
  backgroundColor: statusColor,
  width: cardWidth,
  height: cardHeight,
  top: cardTop,
  left: cardLeft,
  color: '#1F2937',
  padding: '4px 8px 4px 10px',
  overflow: 'hidden',
  cursor: 'pointer',
  zIndex: cardZIndex,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  boxSizing: 'border-box',
  borderRadius: '6px',
  border: '1px solid rgba(0,0,0,0.05)',
  borderLeft: '3px solid rgba(0,0,0,0.1)',
  containerType: 'size',
  fontSize: 'clamp(0.65rem, 1vw, 0.75rem)',
  transition: 'box-shadow 0.3s ease',
  boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
  ...(isHighlighted && {
    animation: `${highlightInward} 2.5s ease-out forwards`,
    border: 'none',
    borderLeft: 'none',
    zIndex: (cardZIndex || 10) + 100,
    overflow: 'visible',
    outline: '2px solid #FFB74D',
    outlineOffset: '-1px',
    '&::before': {
      content: '"N"',
      position: 'absolute',
      top: '-8px',
      left: '-8px',
      width: '18px',
      height: '18px',
      borderRadius: '50%',
      background: '#FFB74D',
      color: '#fff',
      fontSize: '10px',
      fontWeight: 800,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      animation: `${highlightBadgePop} 5s ease-out forwards, ${highlightBadgePulse} 1s ease-in-out 3`,
      zIndex: 1,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 'inherit',
      backgroundColor: 'rgba(255,255,255,0.15)',
      animation: `${highlightFlash} 2.5s ease-out forwards`,
      pointerEvents: 'none',
    },
  }),
  '&:hover': {
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
}));

export const EventCardHorizontalContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '2px',
  overflow: 'hidden',
  flex: 1,
  minWidth: 0,
  '@container (height < 30px)': {
    flexDirection: 'row',
    alignItems: 'center',
    gap: '6px',
    '& p': { fontSize: '10px' },
  },
});

export const EventCardHorizontalTitle = styled('p')({
  margin: 0,
  fontSize: '12px',
  fontWeight: 700,
  color: '#111827',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  lineHeight: 1.3,
  letterSpacing: '-0.2px',
});

export const EventCardHorizontalInfo = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '1px',
  '@container (height < 30px)': {
    flexDirection: 'row',
    gap: '6px',
  },
});

export const EventCardHorizontalTimeRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  fontSize: '10px',
  fontWeight: 500,
  color: '#6B7280',
  lineHeight: 1.3,
  whiteSpace: 'nowrap',
  '@container (height < 30px)': {
    fontSize: '9px',
  },
  '@container (width < 120px)': {
    overflow: 'hidden',
  },
});

export const EventCardHorizontalCategory = styled('p')({
  margin: 0,
  fontSize: '10px',
  fontWeight: 500,
  color: '#9CA3AF',
  lineHeight: 1.3,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  '@container (height < 65px)': {
    display: 'none',
  },
});

export const EventCardHorizontalBadge = styled('div', {
  shouldForwardProp: prop => prop !== 'bgColor',
})(({ bgColor }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: bgColor,
  padding: '2px 8px',
  borderRadius: '6px',
  flexShrink: 0,
  marginLeft: '8px',
}));

export const EventCardHorizontalBadgeText = styled('span', {
  shouldForwardProp: prop => prop !== 'textColor',
})(({ textColor }) => ({
  color: textColor,
  fontWeight: 700,
  fontSize: '10px',
  whiteSpace: 'nowrap',
  lineHeight: 1.5,
  letterSpacing: '-0.2px',
}));

// ============================================
// Event Card - 세로 레이아웃 (4~5개)
// ============================================
export const EventCard = styled('div', {
  shouldForwardProp: prop =>
    !['statusColor', 'cardWidth', 'cardHeight', 'cardTop', 'cardLeft', 'cardZIndex', 'isHighlighted'].includes(prop),
})(({ statusColor, cardWidth, cardHeight, cardTop, cardLeft, cardZIndex, isHighlighted }) => ({
  position: 'absolute',
  backgroundColor: statusColor,
  width: cardWidth,
  height: cardHeight,
  top: cardTop,
  left: cardLeft,
  color: '#1F2937',
  padding: '4px 6px',
  overflow: 'hidden',
  cursor: 'pointer',
  zIndex: cardZIndex,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '2px',
  boxSizing: 'border-box',
  borderRadius: '6px',
  border: '1px solid rgba(0,0,0,0.05)',
  borderLeft: '3px solid rgba(0,0,0,0.1)',
  containerType: 'size',
  transition: 'box-shadow 0.3s ease',
  boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
  ...(isHighlighted && {
    animation: `${highlightInward} 2.5s ease-out forwards`,
    border: 'none',
    borderLeft: 'none',
    zIndex: (cardZIndex || 10) + 100,
    overflow: 'visible',
    outline: '2px solid #FFB74D',
    outlineOffset: '-1px',
    '&::before': {
      content: '"N"',
      position: 'absolute',
      top: '-8px',
      left: '-8px',
      width: '18px',
      height: '18px',
      borderRadius: '50%',
      background: '#FFB74D',
      color: '#fff',
      fontSize: '10px',
      fontWeight: 800,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      animation: `${highlightBadgePop} 5s ease-out forwards, ${highlightBadgePulse} 1s ease-in-out 3`,
      zIndex: 1,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 'inherit',
      backgroundColor: 'rgba(255,255,255,0.15)',
      animation: `${highlightFlash} 2.5s ease-out forwards`,
      pointerEvents: 'none',
    },
  }),
  '&:hover': {
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  '@container (width < 100px)': {
    padding: '2px 4px',
    gap: '1px',
  },
}));

export const EventCardHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4px',
  overflow: 'hidden',
  flexWrap: 'wrap',
  '@container (height < 30px)': {
    gap: '2px',
  },
  '@container (width < 100px)': {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '1px',
  },
});

export const EventCardBadge = styled('div', {
  shouldForwardProp: prop => prop !== 'bgColor',
})(({ bgColor }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: bgColor,
  padding: '0 6px',
  borderRadius: '21px',
  height: '20px',
  minWidth: '33px',
  flexShrink: 0,
  '@container (width < 100px)': {
    height: '16px',
    padding: '0 4px',
    minWidth: '28px',
  },
}));

export const EventCardBadgeText = styled('span', {
  shouldForwardProp: prop => prop !== 'textColor',
})(({ textColor }) => ({
  color: textColor,
  fontWeight: 600,
  fontSize: '11px',
  whiteSpace: 'nowrap',
  '@container (width < 100px)': {
    fontSize: '10px',
  },
}));

export const EventCardTitle = styled('p')({
  margin: 0,
  fontSize: '13px',
  fontWeight: 600,
  color: '#262626',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  lineHeight: 1.3,
  width: '100%',
  minWidth: 0,
  '@container (width < 100px)': {
    fontSize: '11px',
    lineHeight: 1.2,
  },
});

export const EventCardTime = styled('p')({
  margin: 0,
  fontSize: '11px',
  fontWeight: 500,
  color: '#595959',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  lineHeight: 1.2,
  textAlign: 'center',
  '@container (width < 100px)': {
    fontSize: '10px',
    textAlign: 'left',
    lineHeight: 1.1,
  },
});

export const EventCardTrainer = styled('p')({
  margin: 0,
  fontSize: '11px',
  fontWeight: 600,
  color: '#595959',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  lineHeight: 1.2,
  textAlign: 'center',
  '@container (width < 100px)': {
    fontSize: '10px',
    textAlign: 'left',
    lineHeight: 1.1,
  },
});

// Legacy - kept for compatibility
export const EventCardContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  overflow: 'hidden',
  flex: 1,
  justifyContent: 'center',
});

// ============================================
// Current Time Indicator
// ============================================
export const CurrentTimeLine = styled('div', {
  shouldForwardProp: prop => prop !== 'topPosition',
})(({ topPosition }) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  top: `${topPosition}px`,
  height: '2px',
  backgroundColor: '#EF4444',
  zIndex: 100,
  pointerEvents: 'none',
}));

export const CurrentTimeDot = styled('div')({
  position: 'absolute',
  left: '-4px',
  top: '-3px',
  width: '8px',
  height: '8px',
  backgroundColor: '#EF4444',
  borderRadius: '50%',
  zIndex: 100,
});

// ============================================
// Tooltip Styles
// ============================================
export const TooltipLessonHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  padding: '8px 12px',
  margin: '-10px -10px 0',
  background: '#F5F7F8',
  borderBottom: '1px solid #E0E3E6',
  borderRadius: '8px 8px 0 0',
});

export const TooltipLessonTitle = styled(Typography)({
  color: '#1F2729',
  fontWeight: 700,
  fontSize: '13px',
});

export const TooltipStatusBadge = styled('span', {
  shouldForwardProp: prop => prop !== 'badgeBg' && prop !== 'badgeFont',
})(({ badgeBg, badgeFont }) => ({
  backgroundColor: badgeBg,
  color: badgeFont,
  borderRadius: '999px',
  padding: '2px 9px',
  fontSize: '11px',
  fontWeight: 800,
  flexShrink: 0,
}));

export const TooltipBody = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  margin: '0 -10px -10px',
});

export const TooltipInfoRow = styled(Box)({
  display: 'flex',
  alignItems: 'stretch',
  borderBottom: '1px solid #EDF0F2',
  '&:last-child': {
    borderBottom: 'none',
    '& > span:first-of-type': { borderRadius: '0 0 0 8px' },
  },
});

export const TooltipInfoLabel = styled('span')({
  width: '62px',
  flexShrink: 0,
  fontSize: '11.5px',
  fontWeight: 600,
  color: '#7B8A8F',
  padding: '6px 10px',
  background: '#F5F7F8',
  borderRight: '1px solid #EDF0F2',
  display: 'flex',
  alignItems: 'center',
});

export const TooltipInfoValue = styled('span', {
  shouldForwardProp: prop => prop !== 'valueColor',
})(({ valueColor }) => ({
  fontSize: '12px',
  fontWeight: 700,
  color: valueColor || '#2A3235',
  padding: '6px 12px',
  display: 'flex',
  alignItems: 'center',
}));

// ============================================
// Modal Styles
// ============================================
export const ModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: '#fff',
  borderRadius: '10px',
  border: 'none',
});

export const ConfirmModalContainer = styled(ModalContainer)({
  width: '420px',
  overflow: 'hidden',
});

export const SuccessModalContainer = styled(ModalContainer)({
  width: '440px',
  height: '227px',
});

export const BusyDatesModalContainer = styled(ModalContainer)({
  width: '500px',
  maxHeight: '600px',
  overflow: 'hidden',
});

export const ModalHeader = styled(Box)({
  backgroundColor: '#111',
  height: '52px',
  display: 'flex',
  justifyContent: 'space-between',
  borderTopRightRadius: '10px',
  borderTopLeftRadius: '10px',
  padding: '17px 20px',
});

export const ModalHeaderTitle = styled(Typography)({
  fontSize: '16px',
  color: '#fff',
});

export const ModalContent = styled(Box)({
  overflow: 'auto',
  height: '95%',
  padding: '20px',
});

export const ConfirmModalContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  padding: '32px 20px',
});

export const ConfirmModalTitle = styled(Typography)({
  fontSize: '18px',
  fontWeight: 600,
  color: '#333',
});

export const ConfirmModalText = styled(Typography)({
  textAlign: 'center',
  fontSize: '14px',
  color: '#666',
});

export const ConfirmModalButtonGroup = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '8px',
  padding: '16px 20px',
});

// ============================================
// Menu Styles
// ============================================
export const MenuContainer = styled(Box)({
  width: '230px',
  padding: 0,
  marginTop: '-10px',
  marginBottom: '-10px',
});

export const MenuHeader = styled(Box)({
  backgroundColor: '#4AB3BC',
  padding: '8px 12px',
  borderRadius: '10px 10px 0 0',
});

export const MenuTitle = styled(Typography)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#fff',
});

export const MenuSubtitle = styled(Typography)({
  fontSize: '11px',
  color: 'rgba(255,255,255,0.85)',
  marginTop: '3px',
});

export const MenuBody = styled(Box)({
  padding: '8px 12px',
});

export const MenuRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '4px',
});

export const MenuLabel = styled(Typography)({
  fontSize: '12px',
  color: '#666',
});

export const MenuValue = styled(Typography)({
  fontSize: '12px',
  fontWeight: 500,
  color: '#262626',
});

export const MenuButtonGroup = styled(Box)({
  display: 'flex',
  gap: '6px',
  padding: '0 12px 12px',
});

export const MenuButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'variant',
})(({ variant }) => {
  const baseStyles = {
    flex: 1,
    borderRadius: '6px',
    fontSize: '11px',
    padding: '5.6px 0',
    minWidth: 0,
  };

  if (variant === 'primary') {
    return {
      ...baseStyles,
      backgroundColor: '#4AB3BC',
      color: '#fff',
      '&:hover': { backgroundColor: '#4AB3BC' },
    };
  }
  if (variant === 'secondary') {
    return {
      ...baseStyles,
      borderColor: '#4AB3BC',
      color: '#4AB3BC',
      border: '1px solid #4AB3BC',
      '&:hover': { borderColor: '#4AB3BC', backgroundColor: 'rgba(15, 127, 133, 0.1)' },
    };
  }
  if (variant === 'danger') {
    return {
      ...baseStyles,
      borderColor: '#d32f2f',
      color: '#d32f2f',
      border: '1px solid #d32f2f',
      '&:hover': { borderColor: '#b71c1c', backgroundColor: 'rgba(211, 47, 47, 0.1)' },
    };
  }
  return baseStyles;
});

// ============================================
// Copy Weekly Modal
// ============================================
export const CopyWeeklyModalContainer = styled(ModalContainer)({
  width: '420px',
  overflow: 'hidden',
});

export const CopyWeeklyHeader = styled(Box)({
  backgroundColor: '#262626',
  padding: '16px 20px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const CopyWeeklyHeaderTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#fff',
});

export const CopyWeeklyContent = styled(Box)({
  padding: '24px 20px',
});

export const CopyWeeklyDescription = styled(Typography)({
  fontSize: '14px',
  color: '#666',
  marginBottom: '20px',
});

export const CopyWeeklyFormRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

export const CopyWeeklyLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#333',
  minWidth: '70px',
});

export const CopyWeeklyButtonGroup = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '10px',
  padding: '16px 20px',
  borderTop: '1px solid #eee',
});

// ============================================
// 가이드 모달
// ============================================

const guideSlideIn = keyframes`
  0% { transform: translateX(30px); opacity: 0; }
  40% { transform: translateX(-5px); opacity: 1; }
  60% { transform: translateX(3px); }
  80% { transform: translateX(-1px); }
  100% { transform: translateX(0); }
`;

const guideShine = keyframes`
  0%, 100% { background-position: -200% center; }
  50% { background-position: 200% center; }
`;

const guidePulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(74, 179, 188, 0.4); }
  50% { box-shadow: 0 0 0 6px rgba(74, 179, 188, 0); }
  100% { box-shadow: 0 0 0 0 rgba(74, 179, 188, 0); }
`;

export const GuideButton = styled(Button)({
  fontSize: '13px',
  fontWeight: 500,
  fontFamily: 'Pretendard, sans-serif',
  borderRadius: '20px',
  padding: '4px 14px',
  minWidth: 0,
  whiteSpace: 'nowrap',
  textTransform: 'none',
  background: 'linear-gradient(120deg, #4AB3BC 0%, #6DD5DC 40%, #fff 50%, #6DD5DC 60%, #4AB3BC 100%)',
  backgroundSize: '200% 100%',
  color: '#fff',
  border: '1px solid transparent',
  animation: `${guideSlideIn} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, ${guideShine} 3s ease-in-out 1s infinite, ${guidePulse} 2s ease-in-out 1s 3`,
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'linear-gradient(120deg, #3FA0A8 0%, #4AB3BC 100%)',
    border: '1px solid transparent',
    transform: 'scale(1.05)',
    color: '#fff',
  },
});

export const GuideModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 520,
  maxHeight: '85vh',
  background: '#fff',
  borderRadius: '12px',
  overflow: 'hidden',
  outline: 'none',
  display: 'flex',
  flexDirection: 'column',
});

export const GuideModalHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: '#333',
  padding: '14px 20px',
  flexShrink: 0,
});

export const GuideModalHeaderTitle = styled(Typography)({
  color: '#fff',
  fontSize: '16px',
  fontWeight: 600,
  fontFamily: 'Pretendard, sans-serif',
});

export const GuideModalBody = styled(Box)({
  padding: '24px 24px 24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  overflowY: 'auto',
  '&::-webkit-scrollbar': { width: '6px' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: '#ddd', borderRadius: '3px' },
});

export const GuideSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
});

export const GuideSectionLabel = styled(Typography)({
  color: '#262626',
  fontSize: '15px',
  fontWeight: 600,
  fontFamily: 'Pretendard, sans-serif',
});

export const GuideText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 400,
  color: '#666',
  fontFamily: 'Pretendard, sans-serif',
  lineHeight: 1.7,
});

export const GuideDivider = styled(Box)({
  height: '1px',
  background: '#E8E8E8',
});
